// Screenshot production belongs to the shared document foundation. Callers only
// choose when to capture; no file is uploaded or persisted by this module.
const MAX_BYTES=5*1024*1024;
const MAX_DIMENSION=1920;

const bounded=(promise,message)=>{
 let timer;
 return Promise.race([promise,new Promise((_,reject)=>{timer=setTimeout(()=>reject(new Error(message)),10000);})]).finally(()=>clearTimeout(timer));
};

function loadImage(dataUrl){
 if(typeof dataUrl!=='string'||!dataUrl.startsWith('data:image/png;base64,'))throw new Error('La capture de l’ERP n’a pas pu être lue.');
 const image=new Image();
 return bounded(new Promise((resolve,reject)=>{image.onload=()=>resolve(image);image.onerror=()=>reject(new Error('La capture de l’ERP n’a pas pu être lue.'));image.src=dataUrl;}),'La lecture de la capture a pris trop de temps.');
}

function frameReady(video){
 if(video.readyState>=2&&video.videoWidth&&video.videoHeight)return Promise.resolve();
 return bounded(new Promise((resolve,reject)=>{
  video.onloadeddata=resolve;video.onerror=()=>reject(new Error('La capture vidéo n’a pas pu être lue.'));
 }),'La capture n’a pas fourni d’image. Réessayez ou joignez une image existante.');
}

async function screenshotFile(surface,now){
 const width=surface.videoWidth||surface.naturalWidth,height=surface.videoHeight||surface.naturalHeight;
 if(!width||!height)throw new Error('La capture est vide. Réessayez ou joignez une image existante.');
 const scale=Math.min(1,MAX_DIMENSION/Math.max(width,height));
 const canvas=document.createElement('canvas');canvas.width=Math.max(1,Math.round(width*scale));canvas.height=Math.max(1,Math.round(height*scale));
 const context=canvas.getContext('2d');if(!context)throw new Error('La capture n’est pas disponible dans ce navigateur.');
 context.drawImage(surface,0,0,canvas.width,canvas.height);
 const encode=type=>bounded(new Promise(resolve=>canvas.toBlob(resolve,type,0.85)),'La création de l’image a pris trop de temps.');
 let blob=await encode('image/png');
 if(blob?.size>MAX_BYTES)blob=await encode('image/jpeg');
 if(!blob?.size)throw new Error('La création de la capture a échoué. Réessayez ou joignez une image existante.');
 if(blob.size>MAX_BYTES)throw new Error('La capture dépasse 5 Mo. Joignez une image de plus petite taille.');
 const pad=value=>String(value).padStart(2,'0');
 const timestamp=now.getFullYear()+pad(now.getMonth()+1)+pad(now.getDate())+'-'+pad(now.getHours())+pad(now.getMinutes())+pad(now.getSeconds());
 return new File([blob],'Capture ERP - '+timestamp+(blob.type==='image/jpeg'?'.jpg':'.png'),{type:blob.type,lastModified:now.getTime()});
}

export async function captureDocumentScreenshot({beforeCapture=()=>{},now=new Date()}={}){
 if(window.electron?.isElectron){
  if(typeof window.electron.capturePage!=='function')throw new Error('Mettez à jour l’application pour capturer l’ERP, ou joignez une image existante.');
  await beforeCapture();
  const image=await loadImage(await window.electron.capturePage());
  return screenshotFile(image,now);
 }
 if(!navigator.mediaDevices?.getDisplayMedia)throw new Error('La capture n’est pas disponible dans ce navigateur. Vous pouvez joindre une image existante.');
 let stream,video;
 try{
  // Invoke directly from the click handler, before any await: browsers require
  // transient activation and ask the user to choose the source every time.
  stream=await navigator.mediaDevices.getDisplayMedia({video:{displaySurface:'browser'},audio:false,preferCurrentTab:true,selfBrowserSurface:'include',monitorTypeSurfaces:'exclude',surfaceSwitching:'exclude'});
  const track=stream.getVideoTracks()[0];
  if(!track)throw new Error('Aucune image n’a été sélectionnée.');
  if(track.getSettings?.().displaySurface==='monitor')throw new Error('Sélectionnez l’onglet ou la fenêtre de l’ERP plutôt que l’écran entier.');
  await beforeCapture();
  video=document.createElement('video');video.muted=true;video.playsInline=true;video.srcObject=stream;
  await bounded(video.play(),'La capture n’a pas pu démarrer.');await frameReady(video);
  // Wait for a frame after the dialog has disappeared, not a previously buffered frame.
  if(video.requestVideoFrameCallback)await bounded(new Promise(resolve=>video.requestVideoFrameCallback(resolve)),'La capture n’a pas fourni d’image.');
  return await screenshotFile(video,now);
 }catch(error){
  if(error.name==='NotAllowedError'||error.name==='AbortError')throw new Error('Capture annulée ou non autorisée. Vous pouvez joindre une image existante ou créer le ticket sans capture.');
  throw error;
 }finally{
  stream?.getTracks().forEach(track=>track.stop());
  if(video){video.pause();video.srcObject=null;}
 }
}
